import React, { Fragment, useState, useEffect, useContext, useRef } from 'react';
import { Item, Category, AppActions, AppTranslationHelper } from 'common/types';
import { OrderStatuses } from 'common/types/order';
import { Breadcrumbs } from 'common/components';
import { getVisibleCategories, getVisibleItems } from 'common/assets';
import { Card, Search, VariantModal } from './components';
import styles from './itemList.module.css';
import { appContext } from 'common/contexts';

type ItemListProps = {
  categories: Category[];
  currentCategoryId: string;
  translation: AppTranslationHelper;
  items: Item[];
  services: AppActions;
};


const ItemList: React.FC<ItemListProps> = ({ translation, categories, items, currentCategoryId, services }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroupItem, setSelectedGroupItem] = useState<Item | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [context, actions, views, helpers] = useContext(appContext);
  const { t } = translation;
  
  // helpers
  const visibleCategories = getVisibleCategories(currentCategoryId, categories);
  
  // Filter out variant items from the main list
  const filteredItems = items.filter(item => item.type !== 'variant');
  const visibleItems = getVisibleItems(currentCategoryId, filteredItems, searchTerm);
  
  // Get variants for the selected group item
  const getVariantsForGroup = (groupId: string) => {
    return items.filter(item => item.type === 'variant' && item.parentItemId === groupId);
  };

  // handlers
  const changeCurrentCategory = (categoryId: string) => services.category.select(categoryId);
  
  const handleItemClick = (product: Item) => {
    if (product.type === 'group') {
      setSelectedGroupItem(product);
      setIsModalOpen(true);
    } else {
      // Si es una variante y tiene itemParentName, concatenar al nombre para mostrarlo
      if (product.type === 'variant' && product.itemParentName) {
        const productWithCombinedName = {
          ...product,
          name: `${product.itemParentName} ${product.name}`
        };
        services.orders.addItem(productWithCombinedName);
      } else {
        services.orders.addItem(product);
      }
    }
  };

  // render items
  const renderItems = (items: Item[]) => {
    if (!items.length) {
      return <div className={styles.empty}>{t('admin.items.emptyListMessage')}</div>;
    }
    return (
      <Fragment>
        {items.map((product) => (
          <Card
            color={product.color}
            translation={translation}
            key={`product-${product.id}`}
            label={product.name}
            onClick={() => handleItemClick(product)}
            picture={product.picture}
            price={product.price}
            isGroup={product.type === 'group'}
          />
        ))}
      </Fragment>
    );
  };

  // render filtered content
  const renderFilteredItems = () => <Fragment>{renderItems(visibleItems)}</Fragment>;

  // render visible categories and items
  const renderContent = () => (
    <Fragment>
      {visibleCategories.map((category) => (
        <Card
          color={category.color}
          translation={translation}
          key={`category-${category.id}`}
          label={category.name}
          onClick={() => changeCurrentCategory(category.id)}
          picture={category.picture}
        />
      ))}
      {!!visibleCategories.length && <div className={styles.divider} />}
      {renderItems(visibleItems)}
    </Fragment>
  );

  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <Breadcrumbs categories={categories} currentCategoryId={currentCategoryId} onChange={changeCurrentCategory} />
        <Search
          onChange={setSearchTerm}
          onEnter={() => {
            if (visibleItems.length === 1) {
              handleItemClick(visibleItems[0]);
              setSearchTerm('');
            }
          }}
        />
      </div>
      <div className={styles.body}>{!searchTerm ? renderContent() : renderFilteredItems()}</div>
      
      {/* Modal para mostrar variantes */}
      <VariantModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        parentItem={selectedGroupItem}
        variants={selectedGroupItem ? getVariantsForGroup(selectedGroupItem.id) : []}
        translation={translation}
        onSelectVariant={(variant) => services.orders.addItem(variant)}
      />
    </div>
  );
};

export default ItemList;
